import {
  Menu,
  Dropdown,
  Button,
  Icons,
  Space,
  Tag,
  Input,
  Empty,
  Divider,
} from '@pankod/refine-antd'
import { useRouter } from 'next/router'
import React, { useRef, useState } from 'react'
import {
  useCan,
  useCreate,
  useCustomMutation,
  useInvalidate,
  useNavigation,
  useOne,
  useUpdate,
} from '@pankod/refine-core'
import clsx from 'clsx'

import { DrawerEditGrade } from '@resources/lms-management/program/program-setup/blocks/DrawerEditGrade'
import { CertificateSettingsDrawer } from '../../program-setup/blocks/DrawerCertificateSettings'
import ModulDrawer, {
  useModulDrawer,
} from '@resources/lms-management/program/program-setup/blocks/program-module/ModulDrawer'
import PengajarPraktikConfigDrawer, {
  usePengajarPraktikConfigDrawer,
} from '@resources/lms-management/program/program-setup/blocks/jurnal-pengajar-praktik/PengajarPraktikJurnalDrawer'
import JurnalFasilitasiDrawer, {
  useJurnalFasilitasiDrawer,
} from '@resources/lms-management/program/program-setup/blocks/jurnal-fasilitasi/JurnalFasilitasiDrawer'
import {
  FINALIZATION_CONFIG_STAGE,
  PROGRAM_STATE,
  TLMSProgramDetail,
} from '../../types'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import PenilaianKinerjaDrawer, {
  usePenilaianKinerjaDrawer,
} from '../../program-setup/blocks/penilaian-kinerja/PenilaianKinerjaDrawer'
import MenuItemPopconfirm from './MenuItemPopconfirm'
import { TCommonError } from 'src/interfaces/common'
import PresensiDrawer, {
  usePresensiDrawer,
} from '../../program-setup/blocks/presensi/PresensiDrawer'
import PembobotanPenilaianPPGConfigDrawer, {
  usePembobotanPenilaianPPGConfigDrawer,
} from '../../program-setup/blocks/pembobotan-penilaian-pgp/PembobotanPenilaianPGPDrawer'
import BlueprintRegistrationDrawer, {
  useBlueprintRegistrationDrawer,
} from '../../program-setup/blocks/blueprint/BlueprintRegistrationDrawer'
import UmpanBalikProgramDrawer, {
  useUmpanBalikProgramDrawer,
} from '../../program-setup/blocks/umpan-balik-program/UmpanBalikProgramDrawer'
import PeriodePenilaianDrawer, {
  usePeriodePenilaianDrawer,
} from '../../program-setup/blocks/periode-penilaian/PeriodePenilaianDrawer'
import PenilaianKinerjaCategoryDrawer, {
  usePenilaianKinerjaCategoryDrawer,
} from '../../program-setup/blocks/penilaian-kinerja-category/PenilaianKinerjaCategoryDrawer'
import ProgramCopyConfigDrawer, {
  useProgramCopyConfigDrawer,
} from '../../program-setup/blocks/program-copy-config/ProgramCopyConfigDrawer'
import DasusFormPengkiDrawerProps, {
  useDasusFormPengkiDrawer,
} from '../../program-setup/blocks/dasus-form-pengki-config'
import { useProgramStore } from '../../store'
import PaketModulDrawer, {
  usePaketModulConfigDrawer,
} from '../../program-setup/blocks/paket-modul/PaketModulDrawer'
import ActivityListDrawer, {
  useActivityListDrawer,
} from '../../program-setup/blocks/blueprint/ActivityListDrawer'
import ActivityGroupDrawer, {
  useActivityGroupDrawer,
} from '../../program-setup/blocks/activity-group/ActivityGroupDrawer'
import DrawerBuktiKaryaConfig from '../../program-setup/blocks/DrawerBuktiKaryaConfig'
import { Highlighter, filterMenuItemsRecursive } from './utils'
import WebinarDrawer, {
  useWebinarConfigDrawer,
} from '../../program-setup/blocks/webinar/WebinarDrawer'
import useCreateDasusOffline from '../../program-setup/blocks/useCreateDasusOffline'
import SyncClassesButton from '../../program-setup/blocks/SyncClassesButton'
import CertificateParticipantFlagUploadButton from '../CertificateParticipantFlagUploadButton'
import BulkImportParticipantDrawer, {
  useBulkImportParticipantDrawer,
} from '../../program-setup/blocks/bulk-import-participant/BulkImportParticipantDrawer'
import ProgramVerificationDrawer, {
  useProgramVerificationDrawer,
} from '../../program-setup/blocks/program-verification/ProgramVerificationDrawer'
import ParticipantMapperUploadButton from '../ParticipantMapperUploadButton'
import ResetSertifikatModal, {
  useResetSertifikatModal,
} from '../../program-setup/blocks/reset-sertifikat/ResetSertifikatModal'
import InstructorDrawer, {
  useInstructorDrawer,
} from '../../program-setup/blocks/webinar/InstructorDrawer'
import SelfEnrollmentConfigDrawer, {
  useSelfEnrollmentConfigDrawer,
} from '../../program-setup/blocks/self-enrollment/SelfEnrollmentConfigDrawer'

import type { InputRef } from 'antd'

const TagElement = ({
  activeText = 'FINALIZED',
  inactiveText = 'DRAFT',
  ...props
}: {
  active: boolean
  activeText?: string
  inactiveText?: string
  className?: string
}) => (
  <Tag
    className={clsx('!m-0', props.className)}
    color={props.active ? 'processing' : 'default'}
  >
    {props.active ? activeText : inactiveText}
  </Tag>
)

type ConfigurationButtonProps = {
  className?: string
  disabled?: boolean
  initialSisIdAccount?: string
  jurnalFasilitasiConfigStage?: TLMSProgramDetail['jurnalFasilitasiConfigStage']
  penilaianKinerjaConfigStage?: TLMSProgramDetail['penilaianKinerjaConfigStage']
  ppConfigStage?: TLMSProgramDetail['ppConfigStage']
  programId?: string | number
  programName?: string
  programStatus?: TLMSProgramDetail['status']
  timeTrackerActive?: TLMSProgramDetail['timeTrackerActive']
  umpanBalikConfigStage?: TLMSProgramDetail['umpanBalikConfigStage']
  useNewCertificate?: TLMSProgramDetail['useNewCertificate']
  modifyProfileActive?: TLMSProgramDetail['modifyProfileActive']
  autoApproveModifyProfileActive?: TLMSProgramDetail['autoApproveModifyProfileActive']
  autoApproveDiklatParticipantsActive?: TLMSProgramDetail['autoApproveDiklatParticipantsActive']
  isSelfEnrollment?: TLMSProgramDetail['isSelfEnrollment']
}
const ConfigurationButton = ({
  className,
  disabled,
  initialSisIdAccount,
  jurnalFasilitasiConfigStage,
  penilaianKinerjaConfigStage,
  ppConfigStage,
  programId,
  programName,
  programStatus,
  timeTrackerActive,
  umpanBalikConfigStage,
  useNewCertificate,
  modifyProfileActive,
  autoApproveModifyProfileActive,
  autoApproveDiklatParticipantsActive,
  isSelfEnrollment,
}: ConfigurationButtonProps) => {
  const { data } = useOne<{ data: { isActive: boolean } }>({
    dataProviderName: 'lms',
    id: 'flag',
    resource: `programs/${programId}/dummy-participants`,
  })
  const { data: toggleCertificateData } = useOne<{ data: { active: boolean } }>(
    {
      dataProviderName: 'lms',
      id: 'certificate-toggle',
      resource: `programs/${programId}`,
    },
  )
  const { data: hideAddParticipantData } = useOne<{
    data: { isActive: boolean }
  }>({
    dataProviderName: 'lms',
    id: 'flag',
    resource: `programs/${programId}/hide_add_participant`,
  })
  const hideAddParticipantActive =
    hideAddParticipantData?.data?.data?.isActive || false

  const { listUrl } = useNavigation()
  const dummyParticipantActive = data?.data?.data?.isActive
  const toggleCertificateActive = toggleCertificateData?.data?.data?.active
  const isOwner = useProgramStore(({ isOwner }) => isOwner)
  const [open, setOpen] = useState(false)
  const [searchConfig, setSearchConfig] = useState('')
  const inputSearchRef = useRef<InputRef>(null)
  const invalidate = useInvalidate()
  const [editGradeShow, setEditGradeShow] = useState(false)
  const [setupCertificateShow, setSetupCertificateShow] = useState(false)
  const [showBKConfig, setShowBKConfig] = useState(false)
  const { mutate: doToggleTimetracker, isLoading: isLoadingTimetracker } =
    useCreate()
  const {
    mutate: doToggleDummyParticipant,
    isLoading: isLoadingDummyParticipant,
  } = useCreate()
  const { mutateAsync: doToggleModifyProfile } = useCreate()
  const { mutateAsync: doToggleHideAddParticipant } = useCreate()
  const { mutateAsync: doToggleApprovalModifyProfile } = useCreate()
  const { mutateAsync: doToggleAutoApproveDiklatParticipants } = useCreate()
  const { mutateAsync: doToggleSelfEnrollment } = useCreate()
  const {
    mutate: doToggleCertificateApproval,
    isLoading: isLoadingCertificateApproval,
  } = useUpdate()
  const { mutateAsync: doResetCertificate } = useCreate()
  const { show: showModulSetup, ...modulDrawerProps } = useModulDrawer()
  const { show: showPresensi, ...presensiDrawerProps } = usePresensiDrawer()
  const { show: showPenilaianKinerja, ...penilaianKinerjaDrawerProps } =
    usePenilaianKinerjaDrawer()
  const {
    show: showPenilaianKinerjaCategory,
    ...penilaianKinerjaCategoryDrawerProps
  } = usePenilaianKinerjaCategoryDrawer()
  const { show: showPP, ...pengajarPraktikDrawerProps } =
    usePengajarPraktikConfigDrawer()
  const { show: showJurnalFasilitasi, ...jurnalFasilitasiDrawerProps } =
    useJurnalFasilitasiDrawer()
  const {
    show: showBlueprintRegistration,
    ...blueprintRegistrationDrawerProps
  } = useBlueprintRegistrationDrawer()
  const {
    show: showPembobotanPenilaianDrawer,
    ...pembobotanPenilaianDrawerProps
  } = usePembobotanPenilaianPPGConfigDrawer()
  const { show: showUmpanBalikProgram, ...umpanBalikProgramDrawerProps } =
    useUmpanBalikProgramDrawer()
  const { show: showPeriodePenilaian, ...periodePenilaianDrawerProps } =
    usePeriodePenilaianDrawer()
  const { show: showProgramCopyConfig, ...programCopyConfigDrawerProps } =
    useProgramCopyConfigDrawer()
  const { show: showPaketModulConfigDrawer, ...paketModulConfigDrawerProps } =
    usePaketModulConfigDrawer()
  const { show: showWebinarConfigDrawer, ...webinarConfigDrawerProps } =
    useWebinarConfigDrawer()
  const {
    show: showBulkImportParticipantDrawer,
    ...bulkImportParticipantDrawerProps
  } = useBulkImportParticipantDrawer()
  const {
    show: showProgramVerificationDrawer,
    ...programVerificationDrawerProps
  } = useProgramVerificationDrawer()
  const { show: showResetNewSertifikatModal, ...resetNewSertifikatModalProps } =
    useResetSertifikatModal()
  const { show: showInstructorDrawer, ...instructorDrawerProps } =
    useInstructorDrawer()
  const {
    show: showSelfEnrollmentConfigDrawer,
    ...selfEnrollmentConfigDrawerProps
  } = useSelfEnrollmentConfigDrawer()
  const { show: showActivityList, ...activityListDrawerProps } =
    useActivityListDrawer()
  const { show: showActivityGroupList, ...activityGroupListDrawerProps } =
    useActivityGroupDrawer()

  const {
    show: showDasusFormPengkiConfig,
    ...dasusFormPengkiConfigDrawerProps
  } = useDasusFormPengkiDrawer()

  type FinalizeState =
    | 'FINALIZE_UNDEFINED'
    | 'FINALIZE_CONFIRMED'
    | 'FINALIZE_CANCELLED'

  const { mutateAsync: doFinalize } = useCustomMutation<
    {},
    TCommonError,
    void | { finalizeState: FinalizeState }
  >()

  const { doCreate: createProgramDasus, isLoading: isLoadingProgramDasus } =
    useCreateDasusOffline(programId)

  const { data: canRegisterBlueprint } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'blueprint-registration' },
  })

  const { data: canCopyProgramConfig } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'copy-config' },
  })

  const { data: canResetCertificateProgram } = useCan({
    action: 'show',
    resource: 'reset-certificate-program',
  })

  const { data: canDesignCertificateTemplates } = useCan({
    action: 'show',
    resource: 'design-certificate-templates',
  })

  const { data: canToggleCertificateApproval } = useCan({
    action: 'show',
    resource: 'toggle-certificate-approval',
  })

  const { data: canAccessWebinar } = useCan({
    action: 'show',
    resource: 'lms-webinar-management',
  })

  const { data: canConfigureOfflineDasus } = useCan({
    action: 'show',
    resource: 'dasus-management',
  })

  const { data: canSyncClass } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'sync-classes' },
  })

  const { data: canUpdateParticipantCertificateFlag } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'certificate-participant-flag' },
  })

  const { data: canUploadParticipantMapper } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'participant-mapper' },
  })

  const { data: canToggleModifyProfile } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-modify-profile' },
  })

  const { data: canToggleAutoApproveDiklatParticipants } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-auto-approve-diklat-participants' },
  })

  const { data: canToggleHideAddParticipant } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-hide-add-participant' },
  })

  const { data: canAccessSelfEnrollment } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'self-enrollment' },
  })

  const { data: canToggleSelfEnrollment } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-self-enrollment' },
  })

  const { data: canManageSelfEnrollmentConfig } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'self-enrollment-config' },
  })

  const router = useRouter()
  const isPPFinalized = ppConfigStage === FINALIZATION_CONFIG_STAGE.FINALIZED
  const isFasilitasiFinalized =
    jurnalFasilitasiConfigStage === FINALIZATION_CONFIG_STAGE.FINALIZED
  const isPenilaianKinerjaFinalized =
    penilaianKinerjaConfigStage === FINALIZATION_CONFIG_STAGE.FINALIZED
  const isUmpanBalikFinalized =
    umpanBalikConfigStage === FINALIZATION_CONFIG_STAGE.FINALIZED

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTimeout(() => {
        inputSearchRef.current?.focus()
      }, 100)
    } else {
      setSearchConfig('')
    }
    setOpen(open)
  }

  const handleMenuClick = async (
    callback: () => void | Promise<any>,
    shouldClose: boolean = true,
  ) => {
    await callback()
    setOpen(!shouldClose)
    setSearchConfig('')
  }

  const invalidateProgramDetail = () =>
    invalidate({
      invalidates: ['detail'],
      dataProviderName: 'lms',
      resource: 'programs',
      id: programId,
    })

  const finalizeConfig = async (
    type:
      | 'pengajarPraktik'
      | 'fasilitasi'
      | 'penilaianKinerja'
      | 'umpanBalik'
      | 'periodePenilaian',
    finalizeState:
      | 'FINALIZE_CONFIRMED'
      | 'FINALIZE_CANCELLED' = 'FINALIZE_CONFIRMED',
  ) => {
    const dataByType = {
      pengajarPraktik: {
        title: 'Jurnal asisten',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/pp/${programId}/finalize-config`,
      },
      fasilitasi: {
        title: 'Jurnal fasilitasi',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/jurnal-fasilitasi/finalize-config`,
      },
      penilaianKinerja: {
        title: 'Penilaian kinerja',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/penilaian-kinerja/finalize-config`,
      },
      umpanBalik: {
        title: 'Umpan Balik',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/umpan-balik/finalize-config`,
      },
      periodePenilaian: {
        title: 'Periode Penilaian',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/periode-penilaian/finalize-config`,
      },
    }

    const shouldAppendPayload =
      type === 'umpanBalik' || type === 'penilaianKinerja'

    await doFinalize(
      {
        method: 'post',
        url: dataByType[type].url,
        values: shouldAppendPayload ? { finalizeState } : undefined,
        successNotification: {
          message: `Status ${dataByType[type].title} telah diubah menjadi ${finalizeState === 'FINALIZE_CONFIRMED' ? 'final' : 'draft'}`,
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error: any) =>
          showErrorNotification(
            error,
            `There was a problem when finalizing ${dataByType[type].title}`,
          ),
      },
      {
        onSuccess: () => {
          invalidateProgramDetail()
        },
      },
    )
    handleOpenChange(false)
  }

  const menuItems = [
    {
      type: 'group',
      label: <Highlighter search={searchConfig} text="Nilai (Grading)" />,
      key: 'Nilai (Grading)',
      children: [
        {
          icon: <Icons.ProjectOutlined />,
          label: <Highlighter search={searchConfig} text="Pengaturan Nilai" />,
          key: 'Pengaturan Nilai',
          onClick: () => handleMenuClick(() => setEditGradeShow(true)),
        },
        {
          icon: <Icons.EyeOutlined />,
          label: (
            <Highlighter search={searchConfig} text="Lihat Laporan Nilai" />
          ),
          key: 'Lihat Laporan Nilai',
          onClick: () =>
            handleMenuClick(() =>
              router.push(`/grade-report?programId=${programId}`),
            ),
        },
      ],
    },
    {
      type: 'group',
      label: <Highlighter search={searchConfig} text="Program Modul" />,
      key: 'Program Modul',
      children: [
        {
          icon: <Icons.BookOutlined />,
          label: <Highlighter search={searchConfig} text="Pengaturan Modul" />,
          key: 'Pengaturan Modul',
          onClick: () =>
            handleMenuClick(() => showModulSetup(programId!, programName!)),
        },
        {
          icon: <Icons.BuildFilled />,
          label: (
            <Highlighter search={searchConfig} text="Kelola Paket Modul" />
          ),
          key: 'Kelola Paket Modul',
          onClick: () =>
            handleMenuClick(() => showPaketModulConfigDrawer(programId!)),
        },
        {
          icon: <Icons.AppstoreOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Kelola Kelompok Aktivitas"
            />
          ),
          key: 'Kelola Kelompok Aktivitas',
          onClick: () =>
            handleMenuClick(() =>
              showActivityGroupList(programId!, programStatus),
            ),
        },
        {
          icon: <Icons.CarryOutOutlined />,
          label: (
            <Highlighter search={searchConfig} text="Pengaturan Presensi" />
          ),
          key: 'Pengaturan Presensi',
          onClick: () =>
            handleMenuClick(() => showPresensi(programId!, programName!)),
        },
      ],
    },
    {
      type: 'group',
      key: 'Jurnal Asisten',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={searchConfig} text="Jurnal Asisten" />
          <TagElement active={!!isPPFinalized} />
        </div>
      ),
      children: [
        {
          icon: <Icons.TeamOutlined />,
          label: (
            <Highlighter search={searchConfig} text="Kelola Jurnal Asisten" />
          ),
          key: 'Kelola Jurnal Asisten',
          onClick: () => handleMenuClick(() => showPP(programId!)),
        },
        {
          icon: <Icons.LockOutlined />,
          label: (
            <MenuItemPopconfirm
              title="Apakah Anda yakin ingin melakukan finalisasi Jurnal Asisten?"
              disabled={isPPFinalized}
              onConfirm={async () => {
                await finalizeConfig('pengajarPraktik')
              }}
              placement="right"
              okText="Finalisasi"
              cancelText="Batal"
            >
              <Highlighter
                search={searchConfig}
                text="Finalisasi Jurnal Asisten"
              />
            </MenuItemPopconfirm>
          ),
          key: 'Finalisasi Jurnal Asisten',
          disabled: isPPFinalized,
        },
      ],
    },
    {
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={searchConfig} text="Jurnal Fasilitasi" />
          <TagElement active={!!isFasilitasiFinalized} />
        </div>
      ),
      key: 'Jurnal Fasilitasi',
      children: [
        {
          icon: <Icons.ToolOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Kelola Jurnal Fasilitasi"
            />
          ),
          key: 'Kelola Jurnal Fasilitasi',
          onClick: () =>
            handleMenuClick(() =>
              showJurnalFasilitasi(programId!, programName!),
            ),
        },
        {
          icon: <Icons.LockOutlined />,
          label: (
            <MenuItemPopconfirm
              title="Apakah Anda yakin ingin melakukan finalisasi Jurnal Fasilitasi?"
              disabled={isFasilitasiFinalized}
              onConfirm={async () => {
                await finalizeConfig('fasilitasi')
              }}
              placement="right"
              okText="Finalisasi"
              cancelText="Batal"
            >
              <Highlighter
                search={searchConfig}
                text="Finalisasi Jurnal Fasilitasi"
              />
            </MenuItemPopconfirm>
          ),
          key: 'Finalisasi Jurnal Fasilitasi',
          disabled: isFasilitasiFinalized,
        },
      ],
    },
    {
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={searchConfig} text="Penilaian Kinerja" />
          <TagElement active={!!isPenilaianKinerjaFinalized} />
        </div>
      ),
      key: 'Penilaian Kinerja',
      children: [
        {
          icon: <Icons.PartitionOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Kategori Penilaian Kinerja"
            />
          ),
          key: 'Kategori Penilaian Kinerja',
          onClick: () => {
            handleMenuClick(() =>
              showPenilaianKinerjaCategory(programId!, programName!),
            )
          },
        },
        {
          icon: <Icons.BarChartOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Kelola Penilaian Kinerja"
            />
          ),
          key: 'Kelola Penilaian Kinerja',
          onClick: () =>
            handleMenuClick(() =>
              showPenilaianKinerja(programId!, programName!),
            ),
        },
        {
          icon: isPenilaianKinerjaFinalized ? (
            <Icons.UnlockOutlined />
          ) : (
            <Icons.LockOutlined />
          ),
          label: (
            <MenuItemPopconfirm
              title={
                isPenilaianKinerjaFinalized
                  ? 'Apakah Anda yakin ingin mengubah status Penilaian Kinerja menjadi draft?'
                  : 'Apakah Anda yakin ingin melakukan finalisasi Penilaian Kinerja?'
              }
              onConfirm={async () => {
                await finalizeConfig(
                  'penilaianKinerja',
                  isPenilaianKinerjaFinalized
                    ? 'FINALIZE_CANCELLED'
                    : 'FINALIZE_CONFIRMED',
                )
              }}
              placement="right"
              okText={isPenilaianKinerjaFinalized ? 'Draft' : 'Finalisasi'}
              cancelText="Batal"
            >
              <Highlighter
                search={searchConfig}
                text={
                  isPenilaianKinerjaFinalized
                    ? 'Draft Penilaian Kinerja'
                    : 'Finalisasi Penilaian Kinerja'
                }
              />
            </MenuItemPopconfirm>
          ),
          key: isPenilaianKinerjaFinalized
            ? 'Draft Penilaian Kinerja'
            : 'Finalisasi Penilaian Kinerja',
        },
      ],
    },
    {
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={searchConfig} text="Umpan Balik Program" />
          <TagElement active={!!isUmpanBalikFinalized} />
        </div>
      ),
      key: 'Umpan Balik Program',
      children: [
        {
          icon: <Icons.CommentOutlined />,
          label: (
            <Highlighter search={searchConfig} text="Kelola Umpan Balik" />
          ),
          key: 'Kelola Umpan Balik',
          onClick: () =>
            handleMenuClick(() =>
              showUmpanBalikProgram(programId!, programName!),
            ),
        },
        {
          icon: isUmpanBalikFinalized ? (
            <Icons.UnlockOutlined />
          ) : (
            <Icons.LockOutlined />
          ),
          label: (
            <MenuItemPopconfirm
              title={
                isUmpanBalikFinalized
                  ? 'Apakah Anda yakin ingin mengubah status Umpan Balik Program menjadi draft?'
                  : 'Apakah Anda yakin ingin melakukan finalisasi Umpan Balik Program?'
              }
              onConfirm={async () => {
                await finalizeConfig(
                  'umpanBalik',
                  isUmpanBalikFinalized
                    ? 'FINALIZE_CANCELLED'
                    : 'FINALIZE_CONFIRMED',
                )
              }}
              placement="right"
              okText={isUmpanBalikFinalized ? 'Draft' : 'Finalisasi'}
              cancelText="Batal"
            >
              <Highlighter
                search={searchConfig}
                text={
                  isUmpanBalikFinalized
                    ? 'Draft Umpan Balik'
                    : 'Finalisasi Umpan Balik'
                }
              />
            </MenuItemPopconfirm>
          ),
          key: isUmpanBalikFinalized
            ? 'Draft Umpan Balik'
            : 'Finalisasi Umpan Balik',
        },
      ],
    },
    {
      type: 'group',
      label: <Highlighter search={searchConfig} text="Sertifikat" />,
      key: 'Sertifikat',
      children: [
        ...(canDesignCertificateTemplates?.can || isOwner
          ? [
              {
                icon: <Icons.PictureOutlined />,
                label: (
                  <Highlighter
                    search={searchConfig}
                    text="Kelola Design Template"
                  />
                ),
                key: 'Kelola Design Template',
                onClick: () =>
                  handleMenuClick(() => {
                    const certificateTempalateUrl = listUrl(
                      'certificate-templates',
                    )
                    router.push(
                      `${certificateTempalateUrl}?programId=${programId}`,
                    )
                  }),
              },
            ]
          : []),
        {
          icon: <Icons.FileDoneOutlined />,
          label: (
            <Highlighter search={searchConfig} text="Pengaturan Sertifikat" />
          ),
          key: 'Pengaturan Sertifikat',
          onClick: () => handleMenuClick(() => setSetupCertificateShow(true)),
          disabled: useNewCertificate,
        },
        ...(canResetCertificateProgram?.can || isOwner
          ? [
              {
                icon: <Icons.FileSyncOutlined />,
                danger: true,
                label: useNewCertificate ? (
                  <Highlighter search={searchConfig} text="Reset Sertifikat" />
                ) : (
                  <MenuItemPopconfirm
                    title="Apakah Anda yakin ingin melakukan reset seluruh sertifikat pada program ini?"
                    onConfirm={async () => {
                      return await doResetCertificate({
                        dataProviderName: 'lms',
                        values: {},
                        resource: `v2/programs/${programId}/reset-certificate`,
                        successNotification: {
                          message: 'Sertifikat berhasil direset',
                          type: 'success',
                          description: 'Sukses',
                        },
                        errorNotification: (e) =>
                          showErrorNotification(
                            e,
                            'Terdapat gangguan saat reset sertifikat',
                          ),
                      })
                    }}
                    placement="right"
                    okText="Reset"
                    okButtonProps={{ danger: true }}
                    cancelText="Batal"
                  >
                    <Highlighter
                      search={searchConfig}
                      text="Reset Sertifikat"
                    />
                  </MenuItemPopconfirm>
                ),
                key: 'Reset Sertifikat',
                onClick: () => {
                  if (!useNewCertificate) return
                  handleMenuClick(() => {
                    showResetNewSertifikatModal(programId!)
                  })
                },
              },
            ]
          : []),
        ...(canToggleCertificateApproval?.can || isOwner
          ? [
              {
                key: 'Pengabsahan',
                type: 'group',
                label: (
                  <div className="flex justify-between gap-2">
                    <Highlighter search={searchConfig} text="Pengabsahan" />
                    <TagElement
                      active={!!toggleCertificateActive}
                      activeText="AKTIF"
                      inactiveText="NONAKTIF"
                    />
                  </div>
                ),
                children: [
                  {
                    icon: isLoadingCertificateApproval ? (
                      <Icons.LoadingOutlined spin />
                    ) : toggleCertificateActive ? (
                      <Icons.StopOutlined />
                    ) : (
                      <Icons.FileProtectOutlined />
                    ),
                    key: toggleCertificateActive
                      ? 'Nonaktifkan Pengabsahan (Approval)'
                      : 'Aktifkan Pengabsahan (Approval)',
                    danger: toggleCertificateActive,
                    disabled: isLoadingCertificateApproval,
                    label: (
                      <MenuItemPopconfirm
                        title={`Apakah Anda ingin ${
                          toggleCertificateActive
                            ? 'me-nonaktifkan'
                            : 'mengaktifkan'
                        } pengabsahan (approval) sertifikat?`}
                        okText={
                          toggleCertificateActive ? 'Nonaktifkan' : 'Aktifkan'
                        }
                        cancelText="Batal"
                        disabled={isLoadingCertificateApproval}
                        okButtonProps={{ danger: toggleCertificateActive }}
                        onConfirm={() =>
                          doToggleCertificateApproval({
                            resource: `programs/${programId}`,
                            dataProviderName: 'lms',
                            id: 'certificate-toggle',
                            values: {
                              active: !toggleCertificateActive,
                            },
                            successNotification: {
                              message: `Pengabsahan sertifikat berhasil dikonfigurasi ulang`,
                              type: 'success',
                              description: 'Sukses',
                            },
                            errorNotification: (err) =>
                              showErrorNotification(
                                err,
                                'Terdapat gangguan saat mengubah konfigurasi pengabsahan sertifikat',
                              ),
                          })
                        }
                      >
                        {toggleCertificateActive ? (
                          <Highlighter
                            search={searchConfig}
                            text="Nonaktifkan Pengabsahan (Approval)"
                          />
                        ) : (
                          <Highlighter
                            search={searchConfig}
                            text="Aktifkan Pengabsahan (Approval)"
                          />
                        )}
                      </MenuItemPopconfirm>
                    ),
                  },
                ],
              },
            ]
          : []),
      ],
    },
    {
      type: 'group',
      label: (
        <Highlighter search={searchConfig} text="Pembobotan & Penilaian" />
      ),
      key: 'Pembobotan & Penilaian',
      children: [
        {
          icon: <Icons.PercentageOutlined />,
          label: <Highlighter search={searchConfig} text="Pembobotan Nilai" />,
          key: 'Pembobotan Nilai',
          onClick: () =>
            handleMenuClick(() => showPembobotanPenilaianDrawer(programId!)),
        },
      ],
    },
    {
      type: 'group',
      label: <Highlighter search={searchConfig} text="Manajemen Peserta" />,
      key: 'Manajemen Peserta',
      children: [
        {
          icon: <Icons.UserSwitchOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Kelola Data Peserta (Tersier)"
            />
          ),
          key: 'Kelola Data Peserta (Tersier)',
          onClick: () =>
            handleMenuClick(() =>
              router.push(`/participant-management?programId=${programId}`),
            ),
        },
        {
          key: 'Auto Approve Peserta Diklat',
          type: 'group',
          label: (
            <div className="flex justify-between gap-2">
              <Highlighter
                search={searchConfig}
                text="Auto Approve Peserta Diklat"
              />
              <TagElement
                active={!!autoApproveDiklatParticipantsActive}
                activeText="AKTIF"
                inactiveText="NONAKTIF"
              />
            </div>
          ),
          children: [
            {
              icon: autoApproveDiklatParticipantsActive ? (
                <Icons.StopOutlined />
              ) : (
                <Icons.SafetyOutlined />
              ),
              key: autoApproveDiklatParticipantsActive
                ? 'Nonaktifkan Auto Approve Peserta Diklat'
                : 'Aktifkan Auto Approve Peserta Diklat',
              danger:
                autoApproveDiklatParticipantsActive &&
                (canToggleAutoApproveDiklatParticipants?.can || isOwner),
              disabled: !(
                canToggleAutoApproveDiklatParticipants?.can || isOwner
              ),
              label: (
                <MenuItemPopconfirm
                  title={`Apakah Anda ingin ${
                    autoApproveDiklatParticipantsActive
                      ? 'me-nonaktifkan'
                      : 'mengaktifkan'
                  } fitur Auto Approve Peserta Diklat?`}
                  okText={
                    autoApproveDiklatParticipantsActive
                      ? 'Nonaktifkan'
                      : 'Aktifkan'
                  }
                  cancelText="Batal"
                  disabled={
                    !(canToggleAutoApproveDiklatParticipants?.can || isOwner)
                  }
                  okButtonProps={{
                    danger: autoApproveDiklatParticipantsActive,
                  }}
                  onConfirm={() =>
                    doToggleAutoApproveDiklatParticipants(
                      {
                        resource: `programs/${programId}/auto-approve-diklat-participants`,
                        dataProviderName: 'lms',
                        metaData: {
                          httpMethod: 'post',
                        },
                        values: {
                          active: !autoApproveDiklatParticipantsActive,
                        },
                        successNotification: {
                          message: `Fitur auto approve peserta diklat berhasil dikonfigurasi ulang`,
                          type: 'success',
                          description: 'Sukses',
                        },
                        errorNotification: (err) =>
                          showErrorNotification(
                            err,
                            'Terdapat gangguan saat mengubah konfigurasi auto approve peserta diklat',
                          ),
                      },
                      {
                        onSuccess: () => {
                          invalidate({
                            invalidates: ['detail'],
                            dataProviderName: 'lms',
                            resource: 'programs',
                            id: programId,
                          })
                        },
                      },
                    )
                  }
                >
                  {autoApproveDiklatParticipantsActive ? (
                    <Highlighter
                      search={searchConfig}
                      text="Nonaktifkan Auto Approve Peserta Diklat"
                    />
                  ) : (
                    <Highlighter
                      search={searchConfig}
                      text="Aktifkan Auto Approve Peserta Diklat"
                    />
                  )}
                </MenuItemPopconfirm>
              ),
            },
          ],
        },
        ...(canToggleHideAddParticipant?.can
          ? [
              {
                key: 'Hide Tambah Peserta',
                type: 'group',
                label: (
                  <div className="flex justify-between gap-2">
                    <Highlighter
                      search={searchConfig}
                      text="Hide Tambah Peserta"
                    />
                    <TagElement
                      active={!!hideAddParticipantActive}
                      activeText="AKTIF"
                      inactiveText="NONAKTIF"
                    />
                  </div>
                ),
                children: [
                  {
                    icon: hideAddParticipantActive ? (
                      <Icons.StopOutlined />
                    ) : (
                      <Icons.UsergroupAddOutlined />
                    ),
                    key: hideAddParticipantActive
                      ? 'Nonaktifkan Hide Tambah Peserta'
                      : 'Aktifkan Hide Tambah Peserta',
                    danger:
                      hideAddParticipantActive &&
                      canToggleHideAddParticipant?.can,
                    disabled: !canToggleHideAddParticipant?.can,
                    label: (
                      <MenuItemPopconfirm
                        title={`Apakah Anda ingin ${
                          hideAddParticipantActive
                            ? 'me-nonaktifkan'
                            : 'mengaktifkan'
                        } fitur hide tambah peserta?`}
                        okText={
                          hideAddParticipantActive ? 'Nonaktifkan' : 'Aktifkan'
                        }
                        cancelText="Batal"
                        disabled={!canToggleHideAddParticipant?.can}
                        okButtonProps={{ danger: hideAddParticipantActive }}
                        onConfirm={() =>
                          doToggleHideAddParticipant(
                            {
                              resource: `programs/${programId}/hide_add_participant/flag`,
                              dataProviderName: 'lms',
                              metaData: {
                                httpMethod: 'put',
                              },
                              values: {
                                isActive: !hideAddParticipantActive,
                              },
                              successNotification: {
                                message: `Fitur hide tambah peserta berhasil dikonfigurasi ulang`,
                                type: 'success',
                                description: 'Sukses',
                              },
                              errorNotification: (err) =>
                                showErrorNotification(
                                  err,
                                  'Terdapat gangguan saat mengubah konfigurasi hide tambah peserta',
                                ),
                            },
                            {
                              onSuccess: () => {
                                invalidate({
                                  invalidates: ['detail'],
                                  dataProviderName: 'lms',
                                  resource: `programs/${programId}/hide_add_participant`,
                                  id: 'flag',
                                })
                              },
                            },
                          )
                        }
                      >
                        {hideAddParticipantActive ? (
                          <Highlighter
                            search={searchConfig}
                            text="Nonaktifkan Hide Tambah Peserta"
                          />
                        ) : (
                          <Highlighter
                            search={searchConfig}
                            text="Aktifkan Hide Tambah Peserta"
                          />
                        )}
                      </MenuItemPopconfirm>
                    ),
                  },
                ],
              },
            ]
          : []),
        {
          key: 'Ubah Profil (Primer)',
          type: 'group',
          label: (
            <div className="flex justify-between gap-2">
              <Highlighter search={searchConfig} text="Ubah Profil (Primer)" />
              <TagElement
                active={!!modifyProfileActive}
                activeText="AKTIF"
                inactiveText="NONAKTIF"
              />
            </div>
          ),
          children: [
            {
              icon: modifyProfileActive ? (
                <Icons.StopOutlined />
              ) : (
                <Icons.FormOutlined />
              ),
              key: modifyProfileActive
                ? 'Nonaktifkan Ubah Profil (Primer)'
                : 'Aktifkan Ubah Profil (Primer)',
              danger:
                modifyProfileActive && (canToggleModifyProfile?.can || isOwner),
              disabled: !(canToggleModifyProfile?.can || isOwner),
              label: (
                <MenuItemPopconfirm
                  title={`Apakah Anda ingin ${
                    modifyProfileActive ? 'me-nonaktifkan' : 'mengaktifkan'
                  } fitur ubah data profil (primer)?`}
                  okText={modifyProfileActive ? 'Nonaktifkan' : 'Aktifkan'}
                  cancelText="Batal"
                  disabled={!(canToggleModifyProfile?.can || isOwner)}
                  okButtonProps={{ danger: modifyProfileActive }}
                  onConfirm={() =>
                    doToggleModifyProfile(
                      {
                        resource: `programs/${programId}/modify-profile`,
                        dataProviderName: 'lms',
                        values: {
                          active: !modifyProfileActive,
                        },
                        successNotification: {
                          message: `Fitur ubah data profil (primer) berhasil dikonfigurasi ulang`,
                          type: 'success',
                          description: 'Sukses',
                        },
                        errorNotification: (err) =>
                          showErrorNotification(
                            err,
                            'Terdapat gangguan saat mengubah konfigurasi ubah data profil (primer)',
                          ),
                      },
                      {
                        onSuccess: () => {
                          invalidate({
                            invalidates: ['detail'],
                            dataProviderName: 'lms',
                            resource: `programs`,
                            id: programId,
                          })
                        },
                      },
                    )
                  }
                >
                  {modifyProfileActive ? (
                    <Highlighter
                      search={searchConfig}
                      text="Nonaktifkan Ubah Profil (Primer)"
                    />
                  ) : (
                    <Highlighter
                      search={searchConfig}
                      text="Aktifkan Ubah Profil (Primer)"
                    />
                  )}
                </MenuItemPopconfirm>
              ),
            },
            {
              key: 'Auto-approve Pengajuan',
              type: 'group',
              label: (
                <div className="flex justify-between gap-2">
                  <Highlighter
                    search={searchConfig}
                    text="Auto-approve Pengajuan"
                  />
                  <TagElement
                    active={!!autoApproveModifyProfileActive}
                    activeText="AKTIF"
                    inactiveText="NONAKTIF"
                  />
                </div>
              ),
              children: [
                {
                  icon: autoApproveModifyProfileActive ? (
                    <Icons.StopOutlined />
                  ) : (
                    <Icons.CheckSquareOutlined />
                  ),
                  key: autoApproveModifyProfileActive
                    ? 'Nonaktifkan Auto-approve Ubah Profil'
                    : 'Aktifkan Auto-approve Ubah Profil',
                  danger:
                    autoApproveModifyProfileActive &&
                    (canToggleModifyProfile?.can || isOwner) &&
                    modifyProfileActive,
                  disabled:
                    !modifyProfileActive ||
                    !(canToggleModifyProfile?.can || isOwner),
                  label: (
                    <MenuItemPopconfirm
                      title={`Apakah Anda ingin ${
                        autoApproveModifyProfileActive
                          ? 'me-nonaktifkan'
                          : 'mengaktifkan'
                      } fitur auto-approve ubah data profil (primer)?`}
                      okText={
                        autoApproveModifyProfileActive
                          ? 'Nonaktifkan'
                          : 'Aktifkan'
                      }
                      cancelText="Batal"
                      disabled={
                        !modifyProfileActive ||
                        !(canToggleModifyProfile?.can || isOwner)
                      }
                      okButtonProps={{ danger: autoApproveModifyProfileActive }}
                      onConfirm={() =>
                        doToggleApprovalModifyProfile(
                          {
                            resource: `programs/${programId}/auto-approve-modify-profile`,
                            dataProviderName: 'lms',
                            values: {
                              active: !autoApproveModifyProfileActive,
                            },
                            successNotification: {
                              message: `Fitur auto-approve ubah data profil (primer) berhasil dikonfigurasi ulang`,
                              type: 'success',
                              description: 'Sukses',
                            },
                            errorNotification: (err) =>
                              showErrorNotification(
                                err,
                                'Terdapat gangguan saat mengubah konfigurasi auto-approve ubah data profil (primer)',
                              ),
                          },
                          {
                            onSuccess: () => {
                              invalidate({
                                invalidates: ['detail'],
                                dataProviderName: 'lms',
                                resource: `programs`,
                                id: programId,
                              })
                            },
                          },
                        )
                      }
                    >
                      {autoApproveModifyProfileActive ? (
                        <Highlighter
                          search={searchConfig}
                          text="Nonaktifkan Auto-approve Ubah Profil"
                        />
                      ) : (
                        <Highlighter
                          search={searchConfig}
                          text="Aktifkan Auto-approve Ubah Profil"
                        />
                      )}
                    </MenuItemPopconfirm>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    ...(canAccessSelfEnrollment?.can || isOwner
      ? [
          {
            key: 'Pendaftaran Mandiri',
            type: 'group',
            label: (
              <div className="flex justify-between gap-2">
                <Highlighter search={searchConfig} text="Pendaftaran Mandiri" />
                <TagElement
                  active={!!isSelfEnrollment}
                  activeText="AKTIF"
                  inactiveText="NONAKTIF"
                />
              </div>
            ),
            children: [
              {
                icon: isSelfEnrollment ? (
                  <Icons.StopOutlined />
                ) : (
                  <Icons.CheckSquareOutlined />
                ),
                key: isSelfEnrollment
                  ? 'Nonaktifkan Pendaftaran Mandiri'
                  : 'Aktifkan Pendaftaran Mandiri',
                danger: isSelfEnrollment,
                disabled: !(canToggleSelfEnrollment?.can || isOwner),
                label: (
                  <MenuItemPopconfirm
                    title={`Apakah Anda ingin ${
                      isSelfEnrollment ? 'me-nonaktifkan' : 'mengaktifkan'
                    } pendaftaran mandiri?`}
                    okText={isSelfEnrollment ? 'Nonaktifkan' : 'Aktifkan'}
                    cancelText="Batal"
                    disabled={!(canToggleSelfEnrollment?.can || isOwner)}
                    okButtonProps={{ danger: !!isSelfEnrollment }}
                    onConfirm={() =>
                      doToggleSelfEnrollment(
                        {
                          resource: `programs/${programId}/self-enrollment/status`,
                          dataProviderName: 'lms',
                          metaData: {
                            httpMethod: 'put',
                          },
                          values: {
                            active: !isSelfEnrollment,
                          },
                          successNotification: {
                            message:
                              'Konfigurasi pendaftaran mandiri berhasil diperbarui',
                            type: 'success',
                            description: 'Sukses',
                          },
                          errorNotification: (err) =>
                            showErrorNotification(
                              err,
                              'Terdapat gangguan saat mengubah status pendaftaran mandiri',
                            ),
                        },
                        {
                          onSuccess: () => {
                            invalidateProgramDetail()
                          },
                        },
                      )
                    }
                  >
                    {isSelfEnrollment ? (
                      <Highlighter
                        search={searchConfig}
                        text="Nonaktifkan Pendaftaran Mandiri"
                      />
                    ) : (
                      <Highlighter
                        search={searchConfig}
                        text="Aktifkan Pendaftaran Mandiri"
                      />
                    )}
                  </MenuItemPopconfirm>
                ),
              },
              {
                icon: <Icons.SettingOutlined />,
                key: 'Kelola Pendaftaran Mandiri',
                disabled: !(canManageSelfEnrollmentConfig?.can || isOwner),
                label: (
                  <Highlighter
                    search={searchConfig}
                    text="Kelola Pendaftaran Mandiri"
                  />
                ),
                onClick: () => {
                  handleMenuClick(() =>
                    showSelfEnrollmentConfigDrawer(programId!),
                  )
                },
              },
            ],
          },
        ]
      : []),
    {
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={searchConfig} text="Time Tracker" />
          <TagElement
            active={!!timeTrackerActive}
            activeText="AKTIF"
            inactiveText="NONAKTIF"
          />
        </div>
      ),
      key: 'Time Tracker',
      children: [
        {
          icon: isLoadingTimetracker ? (
            <Icons.LoadingOutlined spin />
          ) : timeTrackerActive ? (
            <Icons.StopOutlined />
          ) : (
            <Icons.FieldTimeOutlined />
          ),
          key: timeTrackerActive
            ? 'Nonaktifkan Time Tracker'
            : 'Aktifkan Time Tracker',
          danger: timeTrackerActive,
          disabled: isLoadingTimetracker,
          label: (
            <MenuItemPopconfirm
              title={`Apakah Anda ingin ${
                timeTrackerActive ? 'me-nonaktifkan' : 'mengaktifkan'
              } time tracker?`}
              okText={timeTrackerActive ? 'Nonaktifkan' : 'Aktifkan'}
              cancelText="Batal"
              disabled={isLoadingTimetracker}
              okButtonProps={{ danger: timeTrackerActive }}
              onConfirm={() =>
                doToggleTimetracker(
                  {
                    resource: `programs/${programId}/time-tracker`,
                    dataProviderName: 'lms',
                    values: {
                      active: !timeTrackerActive,
                    },
                    successNotification: {
                      message: `Time Tracker berhasil dikonfigurasi ulang`,
                      type: 'success',
                      description: 'Sukses',
                    },
                    errorNotification: (err) =>
                      showErrorNotification(
                        err,
                        'Terdapat gangguan saat mengubah konfigurasi time tracker',
                      ),
                  },
                  {
                    onSuccess: () => {
                      invalidateProgramDetail()
                    },
                  },
                )
              }
            >
              {timeTrackerActive ? (
                <Highlighter
                  search={searchConfig}
                  text="Nonaktifkan Time Tracker"
                />
              ) : (
                <Highlighter
                  search={searchConfig}
                  text="Aktifkan Time Tracker"
                />
              )}
            </MenuItemPopconfirm>
          ),
        },
        {
          key: 'Periode Penilaian',
          type: 'group',
          label: <Highlighter search={searchConfig} text="Periode Penilaian" />,
          children: [
            {
              key: 'Kelola Periode',
              label: (
                <Highlighter search={searchConfig} text="Kelola Periode" />
              ),
              icon: <Icons.ClockCircleOutlined />,
              onClick: () =>
                handleMenuClick(() =>
                  showPeriodePenilaian(programId!, programName!),
                ),
            },
          ],
        },
      ],
    },
    ...((canRegisterBlueprint?.can || isOwner) &&
    programStatus !== PROGRAM_STATE.ENDED
      ? [
          {
            type: 'group',
            label: <Highlighter search={searchConfig} text="Blueprint" />,
            key: 'Blueprint',
            children: [
              ...(programStatus === PROGRAM_STATE.PREPARATION
                ? [
                    {
                      icon: <Icons.BlockOutlined />,
                      label: (
                        <Highlighter
                          search={searchConfig}
                          text="Registrasi Blueprint"
                        />
                      ),
                      key: 'Registrasi Blueprint',
                      onClick: () =>
                        handleMenuClick(() =>
                          showBlueprintRegistration(programId!),
                        ),
                    },
                  ]
                : []),
              {
                icon: <Icons.UnorderedListOutlined />,
                label: (
                  <Highlighter
                    search={searchConfig}
                    text="Daftar Aktivitas Canvas"
                  />
                ),
                key: 'Daftar Aktivitas Canvas',
                onClick: () =>
                  handleMenuClick(() =>
                    showActivityList(programId!, programName!),
                  ),
              },
            ],
          },
        ]
      : []),
    ...(canCopyProgramConfig?.can
      ? [
          {
            type: 'group',
            label: <Highlighter search={searchConfig} text="Program Copy" />,
            key: 'Program Copy',
            children: [
              {
                icon: <Icons.CopyOutlined />,
                label: (
                  <Highlighter
                    search={searchConfig}
                    text="Salin Konfigurasi Program"
                  />
                ),
                key: 'Salin Konfigurasi Program',
                onClick: () => {
                  handleMenuClick(() =>
                    showProgramCopyConfig(programId!, programName!),
                  )
                },
              },
            ],
          },
        ]
      : []),
    {
      type: 'group',
      label: (
        <Highlighter
          search={searchConfig}
          text="Penilaian Kinerja Tidak Langsung"
        />
      ),
      key: 'Penilaian Kinerja Tidak Langsung',
      children: [
        {
          icon: <Icons.FormOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Form Penilaian Kinerja Dasus"
            />
          ),
          key: 'Form Penilaian Kinerja Dasus',
          onClick: () => {
            handleMenuClick(() =>
              showDasusFormPengkiConfig(programId!, programName!),
            )
          },
        },
      ],
    },
    {
      key: 'Dummy Peserta',
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={searchConfig} text="Dummy Peserta" />
          <TagElement
            active={!!dummyParticipantActive}
            activeText="AKTIF"
            inactiveText="NONAKTIF"
          />
        </div>
      ),
      children: [
        {
          icon: isLoadingDummyParticipant ? (
            <Icons.LoadingOutlined spin />
          ) : dummyParticipantActive ? (
            <Icons.StopOutlined />
          ) : (
            <Icons.RobotOutlined />
          ),
          key: dummyParticipantActive
            ? 'Nonaktifkan Dummy Peserta'
            : 'Aktifkan Dummy Peserta',
          danger: dummyParticipantActive,
          disabled: isLoadingDummyParticipant,
          label: (
            <MenuItemPopconfirm
              title={`Apakah Anda ingin ${
                dummyParticipantActive ? 'me-nonaktifkan' : 'mengaktifkan'
              } dummy peserta?`}
              okText={dummyParticipantActive ? 'Nonaktifkan' : 'Aktifkan'}
              cancelText="Batal"
              disabled={isLoadingDummyParticipant}
              okButtonProps={{ danger: dummyParticipantActive }}
              onConfirm={() =>
                doToggleDummyParticipant(
                  {
                    resource: `programs/${programId}/dummy-participants/flag`,
                    dataProviderName: 'lms',
                    values: {
                      isActive: !dummyParticipantActive,
                    },
                    successNotification: {
                      message: `Dummy Peserta berhasil dikonfigurasi ulang`,
                      type: 'success',
                      description: 'Sukses',
                    },
                    errorNotification: (err) =>
                      showErrorNotification(
                        err,
                        'Terdapat gangguan saat mengubah konfigurasi dummy peserta',
                      ),
                  },
                  {
                    onSuccess: () => {
                      invalidate({
                        invalidates: ['detail'],
                        dataProviderName: 'lms',
                        resource: `programs/${programId}/dummy-participants`,
                        id: 'flag',
                      })
                    },
                  },
                )
              }
            >
              {dummyParticipantActive ? (
                <Highlighter
                  search={searchConfig}
                  text="Nonaktifkan Dummy Peserta"
                />
              ) : (
                <Highlighter
                  search={searchConfig}
                  text="Aktifkan Dummy Peserta"
                />
              )}
            </MenuItemPopconfirm>
          ),
        },
      ],
    },
    {
      type: 'group',
      label: <Highlighter search={searchConfig} text="Prasyarat Sertifikat" />,
      key: 'Prasyarat Sertifikat',
      children: [
        {
          icon: <Icons.SettingOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Konfigurasi Prasyarat Sertifikat"
            />
          ),
          key: 'Konfigurasi Prasyarat Sertifikat',
          onClick: () => handleMenuClick(() => setShowBKConfig(true)),
        },
      ],
    },
    ...(canAccessWebinar?.can
      ? [
          {
            type: 'group',
            label: <Highlighter search={searchConfig} text="Webinar" />,
            key: 'Webinar',
            children: [
              {
                icon: <Icons.VideoCameraOutlined />,
                label: (
                  <Highlighter search={searchConfig} text="Kelola Webinar" />
                ),
                key: 'Kelola Webinar',
                onClick: () =>
                  handleMenuClick(() => showWebinarConfigDrawer(programId!)),
              },
              {
                icon: <Icons.UserOutlined />,
                label: (
                  <Highlighter search={searchConfig} text="Daftar Instruktur" />
                ),
                key: 'Daftar Instruktur',
                onClick: () => {
                  handleMenuClick(() => showInstructorDrawer(programId!))
                },
              },
            ],
          },
        ]
      : []),
    {
      type: 'group',
      label: <Highlighter search={searchConfig} text="Konfigurasi Lainnya" />,
      key: 'Konfigurasi Lainnya',
      children: [
        ...(canConfigureOfflineDasus?.can || isOwner
          ? [
              {
                icon: <Icons.SettingOutlined />,
                label: (
                  <Highlighter
                    search={searchConfig}
                    text="Konfigurasi Program Dasus"
                  />
                ),
                key: 'Konfigurasi Program Dasus',
                onClick: () => handleMenuClick(() => createProgramDasus()),
                disabled: isLoadingProgramDasus,
              },
            ]
          : []),
        {
          icon: <Icons.SyncOutlined />,
          label: (
            <SyncClassesButton disabled={!(canSyncClass?.can || isOwner)}>
              <Highlighter search={searchConfig} text="Sinkronisasi Kelas" />
            </SyncClassesButton>
          ),
          key: 'Sinkronisasi Kelas',
          onClick: () => {},
          disabled: !(canSyncClass?.can || isOwner),
        },
        {
          icon: <Icons.FlagOutlined />,
          label: (
            <CertificateParticipantFlagUploadButton
              disabled={!(canUpdateParticipantCertificateFlag?.can || isOwner)}
            >
              <Highlighter
                search={searchConfig}
                text="Unggah CSV Flag Sertifikat"
              />
            </CertificateParticipantFlagUploadButton>
          ),
          key: 'Unggah CSV Flag Sertifikat',
          onClick: () => {},
          disabled: !(canUpdateParticipantCertificateFlag?.can || isOwner),
        },
        {
          label: (
            <ParticipantMapperUploadButton
              disabled={!(canUploadParticipantMapper?.can || isOwner)}
              programId={programId}
            >
              {(loading) => (
                <div className="flex gap-1.5 items-center">
                  {loading ? (
                    <Icons.LoadingOutlined />
                  ) : (
                    <Icons.PartitionOutlined />
                  )}
                  <Highlighter
                    search={searchConfig}
                    text="Unggah Participant Mapper"
                  />
                </div>
              )}
            </ParticipantMapperUploadButton>
          ),
          key: 'Unggah Participant Mapper',
          onClick: () => {},
          disabled: !(canUploadParticipantMapper?.can || isOwner),
        },
        {
          icon: <Icons.UsergroupAddOutlined />,
          label: (
            <Highlighter search={searchConfig} text="Bulk Import Partisipan" />
          ),
          key: 'Bulk Import Partisipan',
          disabled: hideAddParticipantActive,
          onClick: () => {
            handleMenuClick(() => {
              showBulkImportParticipantDrawer(programId!)
            })
          },
        },
        {
          icon: <Icons.AuditOutlined />,
          label: (
            <Highlighter
              search={searchConfig}
              text="Informasi Verifikasi Program"
            />
          ),
          key: 'Informasi Verifikasi Program',
          onClick: () => {
            handleMenuClick(() => {
              showProgramVerificationDrawer(programId!)
            })
          },
        },
      ],
    },
  ].toSorted((a, b) =>
    a.key === 'Konfigurasi Lainnya' ? 1 : a.key.localeCompare(b.key),
  )

  const filteredMenuItems = filterMenuItemsRecursive(menuItems, searchConfig)
  return (
    <>
      <Dropdown
        trigger={['click']}
        overlay={
          <div className="dropdown-search flex flex-col">
            <Input.Search
              placeholder="Cari menu konfigurasi"
              ref={inputSearchRef}
              autoFocus
              className="!w-[calc(100%_-_32px)] mt-3 mx-4"
              value={searchConfig}
              allowClear
              onChange={(e) => {
                setSearchConfig(e.target.value)
              }}
            />
            <Divider className="!my-3 !mb-0" />
            {filteredMenuItems.length ? (
              <Menu
                className="flex flex-col w-fit overflow-auto max-h-80 gap-3 !pb-3 !px-1 !shadow-none"
                items={filteredMenuItems}
              />
            ) : (
              <Empty
                description="Tidak ada konfigurasi"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        }
        overlayClassName="!z-[1000] w-fit max-h-[525px]"
        disabled={disabled}
        visible={open}
        onVisibleChange={handleOpenChange}
      >
        <Button
          icon={<Icons.SettingOutlined className="mr-2" />}
          className={className}
        >
          <Space>
            Konfigurasi Program
            <Icons.DownOutlined
              className={clsx(
                open && '-rotate-180',
                !open && '!delay-100',
                '!transition-transform',
              )}
            />
          </Space>
        </Button>
      </Dropdown>
      <DrawerEditGrade
        onDrawerClose={() => setEditGradeShow(false)}
        onSubmitted={() => setEditGradeShow(false)}
        open={editGradeShow}
        programName={programName}
        selectedId={Number(programId)}
      />
      <CertificateSettingsDrawer
        programId={programId}
        programName={programName}
        visible={setupCertificateShow}
        onClose={() => setSetupCertificateShow(false)}
      />
      <ModulDrawer {...modulDrawerProps} programStatus={programStatus} />
      <PenilaianKinerjaDrawer
        {...penilaianKinerjaDrawerProps}
        isFinalized={isPenilaianKinerjaFinalized}
      />
      <PengajarPraktikConfigDrawer
        {...pengajarPraktikDrawerProps}
        isFinalized={isPPFinalized}
      />
      <UmpanBalikProgramDrawer
        {...umpanBalikProgramDrawerProps}
        isFinalized={isUmpanBalikFinalized}
        programStatus={programStatus}
      />
      <JurnalFasilitasiDrawer {...jurnalFasilitasiDrawerProps} />
      <PresensiDrawer {...presensiDrawerProps} programStatus={programStatus} />
      <PembobotanPenilaianPPGConfigDrawer {...pembobotanPenilaianDrawerProps} />
      <BlueprintRegistrationDrawer
        {...blueprintRegistrationDrawerProps}
        initialSisIdAccount={initialSisIdAccount}
      />
      <PeriodePenilaianDrawer {...periodePenilaianDrawerProps} />
      <PenilaianKinerjaCategoryDrawer
        {...penilaianKinerjaCategoryDrawerProps}
      />
      <ProgramCopyConfigDrawer {...programCopyConfigDrawerProps} />
      <DasusFormPengkiDrawerProps {...dasusFormPengkiConfigDrawerProps} />
      <PaketModulDrawer {...paketModulConfigDrawerProps} />
      <ActivityListDrawer {...activityListDrawerProps} />
      <ActivityGroupDrawer {...activityGroupListDrawerProps} />
      <DrawerBuktiKaryaConfig
        programId={programId}
        show={showBKConfig}
        setShow={setShowBKConfig}
      />
      <WebinarDrawer {...webinarConfigDrawerProps} />
      <BulkImportParticipantDrawer {...bulkImportParticipantDrawerProps} />
      <ProgramVerificationDrawer {...programVerificationDrawerProps} />
      <ResetSertifikatModal {...resetNewSertifikatModalProps} />
      <InstructorDrawer
        useNewCertificate={useNewCertificate}
        {...instructorDrawerProps}
      />
      <SelfEnrollmentConfigDrawer
        {...selfEnrollmentConfigDrawerProps}
        onSubmitted={invalidateProgramDetail}
      />
    </>
  )
}

export default ConfigurationButton
